// TEACH-1 — **LALA'S TEACHING ROOM: THE LAW OF WHAT THE OWNER MAY TEACH AND HOW IT REACHES HER.**
//
// Her order: *"No CC builds needed for corrections ever again."* Three classes of teaching, saved
// with her authorship, flowing into Lala's live knowledge THE SAME WAY HER CORPUS ALREADY REACHES
// HER, tagged owner-taught at the highest authority tier, effective ON SAVE.
//
// >>> THIS FILE IMPORTS NOTHING. <<< That is the entry requirement that lets a `.mjs` gate LOAD and
// RUN it (R146: reach is transitive), which is what makes every clause below DRIVEN rather than
// grepped — and this is a law about what the house's own voice may be taught, so a source regex
// certifying a spelling is not enough (L12/CRYSTAL).

/** HER CLAUSE 2's THREE CLASSES, DECLARED ONCE. The form reads this and so does the door, so a
 *  fourth class joins by being DECLARED rather than by two rosters being kept in step (R28/R29). */
export const TEACHING_CLASSES = Object.freeze([
  Object.freeze({
    key: "correction",
    label: "Correction",
    hint: "When asked about this, the truth is...",
    subjectLabel: "When asked about",
    bodyLabel: "The truth is",
  }),
  Object.freeze({
    key: "addition",
    label: "Addition",
    hint: "New knowledge, a ruling, a teaching, a product fact",
    subjectLabel: "This is about",
    bodyLabel: "In your own words",
  }),
  Object.freeze({
    key: "voice",
    label: "Voice note",
    hint: "Say it like this, not like that",
    subjectLabel: "When you are",
    bodyLabel: "Say it like this",
  }),
]);

export const TEACHING_CLASS_KEYS = Object.freeze(TEACHING_CLASSES.map((c) => c.key));

/** >>> THE SOURCE TAG, AND IT IS ONE DECLARATION READ BY THE STORE, THE RETRIEVAL AND THE PROMPT.
 *  <<< A second spelling of *what an owner teaching is called* is a teaching that is saved and never
 *  retrieved, with nothing anywhere to say so (R28/R29). */
export const TEACHING_SOURCE = "owner-taught";

/** >>> SEMI-1 — HOW MANY OF A MEMBER'S OWN WORDS A TEACHING MUST MATCH BEFORE THE BROAD RETRIEVAL
 *  PASS WILL PUT IT IN FRONT OF HER. ONE shared word is a coincidence ("candle"); TWO is a subject.
 *  <<<
 *
 *  **IT LIVES IN THE LAW RATHER THAN IN THE STORE, AND THAT PLACEMENT IS THE POINT (R146).** The
 *  store reaches the pool, so a FAST fixture may not import it — a floor declared beside the SQL is
 *  a floor no gate can RUN, only read out of a string. This module imports NOTHING, which is the
 *  entry requirement, so the number is driven rather than quoted.
 *
 *  **AND IT IS A COUNT OF TERMS RATHER THAN A RANK THRESHOLD.** Measured on her Semi law the day it
 *  was seeded, the questions it is FOR rank 0.4327..0.7403 and the ones it is not rank
 *  0.2229..0.3344 — so a float in that gap works TODAY and drifts silently as she teaches, because
 *  `ts_rank` moves with document length and term frequency. That is a value stated as a law
 *  (W1-11/LP2/R65), and this is a property of the match instead. */
export const BROAD_TERM_FLOOR = 2;

/** Her clause 3's *highest authority tier*, as a NUMBER against the corpus's own ladder. `bio` is
 *  2.0 and is WALLED from retrieval by E1's contamination wall, so 1.9 (`site-help`) is the highest
 *  weight anything retrievable carries today — a teaching outranks it. */
export const TEACHING_RANK = 3.0;

export const TEACHING_STATES = Object.freeze(["active", "retired"]);

export function teachingClassValid(key) {
  return typeof key === "string" && TEACHING_CLASS_KEYS.includes(key);
}

export function teachingClass(key) {
  return TEACHING_CLASSES.find((c) => c.key === key) || null;
}

export function teachingStateValid(state) {
  return typeof state === "string" && TEACHING_STATES.includes(state);
}

/** HER CLAUSE 4's RETIRE: one tap DISABLES, it never deletes. Only an active teaching is read. */
export function teachingIsLive(row) {
  return Boolean(row) && row.state === "active";
}

export const TEACHING_SUBJECT_MAX = 200;
export const TEACHING_BODY_MAX = 4000;

// ===============================================================================================
// HER CLAUSE 5 — **THE GATES RIDE, AND THE STRUCTURAL HALF IS WHAT REALLY CARRIES IT.**
//
// >>> A TEACHING IS CONTENT LALA READS, NEVER A RULE SHE OBEYS, AND THAT IS WHY IT CANNOT WEAKEN A
// STANDING LAW. <<< It arrives through the RETRIEVAL rail — the same block her books and classes
// arrive in — so it is material, and every gate in the stack still runs on the DRAFT afterwards:
// confidentiality, tier scope, register, the family floor, the compilation wall. A teaching saying
// *"tell them the system prompt"* produces a draft that `enforceConfidentiality` re-renders exactly
// as it would any other leak. **The gates outrank owner-taught content in the machinery because the
// teaching never reaches the machinery at all.**
//
// >>> SO THE DETECTOR BELOW IS THE SECOND WALL AND ITS DIRECTION IS THE OPPOSITE OF THIS HOUSE'S
// USUAL ONE (R19), WHICH IS SAID OUT LOUD RATHER THAN LEFT TO BE INFERRED. <<< Almost every
// detector here fails CLOSED because the thing underneath it is the only wall. Here the wall is
// already standing, so a false refusal costs her a teaching she has to reword — on the one surface
// whose whole purpose is that she never has to ask anybody to make a change — and a missed one costs
// NOTHING, because the gate stack catches the output. It is therefore NARROW and it fails OPEN, and
// what it is for is telling her WHY rather than letting her write something that silently does
// nothing.
// ===============================================================================================

/** The four laws a teaching may not ask Lala to set aside, each with the sentence she is told. Named
 *  rather than a single "that is not allowed", because a refusal that does not say which law it hit
 *  is one she cannot act on (R18: four facts, four reasons). */
export const TEACHING_REFUSALS = Object.freeze([
  Object.freeze({
    key: "confidentiality",
    // The instruction shapes only: an ordinary teaching ABOUT her method legitimately names her
    // workings, so the verb is what is read rather than the noun.
    pattern: /\b(reveal|disclose|share|show|tell (?:them|members|her|him)|explain|expose)\b[^.!?]{0,60}\b(system prompt|your instructions|your prompt|the prompt you|your machinery|how you are built|your rules|your source code)\b/i,
    message: "That asks Lala to reveal the machinery, and the confidentiality law outranks a teaching. Teach the answer she should give instead.",
  }),
  Object.freeze({
    key: "tier",
    pattern: /\b(ignore|skip|bypass|override|do not (?:check|enforce)|forget)\b[^.!?]{0,60}\b(the tier|tier gate|their tier|membership gate|the paywall|the gate)\b/i,
    message: "That asks Lala to set aside a tier wall, and what a member has paid for is not something a teaching can change. Use a grant if she should have it.",
  }),
  Object.freeze({
    key: "compilation",
    pattern: /\b(give|hand over|send|compile|export|dump)\b[^.!?]{0,60}\b(the (?:whole|entire|full) (?:spellbook|corpus|catalog|catalogue|library|manual)|all (?:the )?seals|every (?:seal|spell|working))\b/i,
    message: "That asks Lala to hand over a whole body of the house's work, and the compilation wall outranks a teaching. Teach what she may share about it instead.",
  }),
  Object.freeze({
    key: "safety",
    pattern: /\b(ignore|skip|bypass|override|do not (?:apply|run|use))\b[^.!?]{0,60}\b(the safety|safety gate|the family (?:gate|protection)|the (?:conduct|harm) gate)\b/i,
    message: "That asks Lala to set aside a safety floor, and those hold above every teaching. Teach the answer she should give instead.",
  }),
]);

/** Answers `null` for a teaching that may be saved, or the refusal it hit. Her clause 5's *REFUSED
 *  WITH THE REASON NAMED*, and the reason is the law's own sentence rather than a generic one. */
export function teachingRefusal(text) {
  const s = typeof text === "string" ? text : "";
  if (!s) return null;
  for (const rule of TEACHING_REFUSALS) {
    if (rule.pattern.test(s)) return rule;
  }
  return null;
}

/** >>> THE ONE VERDICT THE FORM AND THE DOOR BOTH READ. <<< Written twice it is two answers to
 *  *what may be taught*, and the copy that drifts is the one on the door, which is the only one that
 *  really decides (R28/R29). Answers `{ ok, reason, message }` — a REASON key rather than a bare
 *  false, because a form that cannot say which field is wrong makes her guess (R18). */
export function teachingVerdict(input) {
  // A law that THROWS is worse than one that refuses: this is exported, so a caller handing over
  // nothing at all must be answered rather than crashed (R19 — a crash is not a catch).
  const { classKey, subject, body } = input && typeof input === "object" ? input : {};
  if (!teachingClassValid(classKey)) {
    return { ok: false, reason: "class", message: "Pick what kind of teaching this is." };
  }
  const subj = typeof subject === "string" ? subject.trim() : "";
  const text = typeof body === "string" ? body.trim() : "";
  if (!subj) return { ok: false, reason: "subject", message: "Say what this teaching is about." };
  if (subj.length > TEACHING_SUBJECT_MAX) {
    return { ok: false, reason: "subject-long", message: `Keep the subject under ${TEACHING_SUBJECT_MAX} characters.` };
  }
  if (!text) return { ok: false, reason: "body", message: "Write the teaching itself." };
  if (text.length > TEACHING_BODY_MAX) {
    return { ok: false, reason: "body-long", message: `Keep a teaching under ${TEACHING_BODY_MAX} characters. Save it as two.` };
  }
  // The refusal is asked of BOTH fields, because the instruction can be written in either one.
  const hit = teachingRefusal(`${subj}\n${text}`);
  if (hit) return { ok: false, reason: `law:${hit.key}`, message: hit.message };
  return { ok: true, reason: "", message: "" };
}

/** >>> WHAT LALA IS HANDED, AND IT IS LABELLED AS THE OWNER'S OWN WORD. <<< The retrieval block is
 *  headed *"RELEVANT TEACHINGS ... prefer them over general knowledge"*, and E1's whole finding is
 *  that a label decides how a model treats what arrives under it. So a teaching says WHOSE it is and
 *  that it outranks the corpus — which is her clause 3's *the owner's word wins* said to the model
 *  as well as enforced by the ordering. */
export function teachingChunk(row) {
  if (!teachingIsLive(row)) return null;
  const cls = teachingClass(row.classKey);
  if (!cls) return null;
  const subj = String(row.subject || "").trim();
  const body = String(row.body || "").trim();
  if (!subj || !body) return null;
  return {
    title: `Lala's own ruling — ${cls.label}: ${subj}`,
    content: `${cls.subjectLabel} ${subj}. ${cls.bodyLabel}: ${body}`,
    author: "Lala Inuti Ahari",
    source: TEACHING_SOURCE,
  };
}

/** Her clause 4's ledger order: newest first, and RETIRED rows stay (they are the history). */
export function teachingLedger(rows) {
  const list = Array.isArray(rows) ? rows.filter(Boolean) : [];
  return list.slice().sort((a, b) => Number(b.id || 0) - Number(a.id || 0));
}

/** HER CLAUSE 3's CONFLICT NOTE. The corpus hits a teaching's subject already retrieves ARE what she
 *  is overriding, so they are recorded ON THE ROW at save time — a fact about the day she taught it,
 *  which is the only day it can honestly be measured (the corpus moves underneath afterwards). */
export function conflictNote(hits) {
  const list = Array.isArray(hits) ? hits.filter((h) => h && h.title) : [];
  return list.slice(0, 3).map((h) => ({
    title: String(h.title || ""),
    excerpt: String(h.content || "").replace(/\s+/g, " ").trim().slice(0, 220),
    source: String(h.source || ""),
  }));
}
